import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Button
} from '@material-ui/core';
import { colors } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import moment from 'moment';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { GetMockBillingPDFData, BillingPdfNotReady } from 'store/actions';

pdfMake.vfs = pdfFonts.pdfMake.vfs;
pdfMake.fonts = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf'
  }
};

const Results = props => {
  const { className, data, ...rest } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const currency = '฿';
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const pdfData = useSelector(({ billing }) => billing.pdfData);
  const pdfReady = useSelector(({ billing }) => billing.pdfReady);

  useEffect(() => {
    if (pdfReady && pdfData) {
      let chargeRow = [];
      let transferRow = [];
      let linkPayRow = [];

      pdfData.charge.forEach(row => {
        chargeRow.push([
          {
            text: row.name,
            marginLeft: 20,
            fillColor: '#ebf0f9',
            alignment: 'left'
          },
          { text: row.qty, fillColor: '#ebf0f9', alignment: 'right' },
          { text: row.unit, fillColor: '#ebf0f9', alignment: 'right' },
          { text: row.amount, fillColor: '#ebf0f9', alignment: 'right' }
        ]);
      });

      pdfData.transfer.forEach(row => {
        transferRow.push([
          {
            text: row.name,
            marginLeft: 20,
            fillColor: '#ebf0f9',
            alignment: 'left'
          },
          { text: row.qty, fillColor: '#ebf0f9', alignment: 'right' },
          { text: row.unit, fillColor: '#ebf0f9', alignment: 'right' },
          { text: row.amount, fillColor: '#ebf0f9', alignment: 'right' }
        ]);
      });

      pdfData.linkPay.forEach(row => {
        linkPayRow.push([
          {
            text: row.name,
            marginLeft: 20,
            fillColor: '#ebf0f9',
            alignment: 'left'
          },
          { text: row.qty, fillColor: '#ebf0f9', alignment: 'right' },
          { text: row.unit, fillColor: '#ebf0f9', alignment: 'right' },
          { text: row.amount, fillColor: '#ebf0f9', alignment: 'right' }
        ]);
      });
      generateBillingPDF(
        pdfData.invoiceNo,
        moment(pdfData.invoiceDate).format('DD-MM-YYYY'),
        pdfData.customer,
        chargeRow,
        transferRow,
        linkPayRow,
        pdfData.totalService,
        pdfData.tax,
        pdfData.totalAmount
      );
    }
  }, [pdfReady, pdfData]);

  const handleChangePage = (event, page) => {
    setPage(page);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(event.target.value);
  };

  const handleProcessBillingPDF = () => {
    dispatch(GetMockBillingPDFData());
  };

  const generateBillingPDF = (
    invoiceNo,
    invoiceDate,
    customer,
    chargeRow,
    transferRow,
    linkPayRow,
    totalService,
    tax,
    totalAmount
  ) => {
    const docDefinition = {
      pageSize: 'A4',
      content: [
        {
          columns: [
            {
              width: '*',
              table: {
                body: [
                  [
                    {
                      border: [true, true, true, false],
                      text: ' ',
                      fillColor: '#90caf9',
                      style: 'customerDetails1'
                    }
                  ],
                  [
                    {
                      border: [true, false, true, false],
                      text: 'Customer Details',
                      fillColor: '#90caf9',
                      style: 'customerDetails2'
                    }
                  ],
                  [
                    {
                      border: [true, false, true, true],
                      text: ' ',
                      fillColor: '#90caf9',
                      style: 'customerDetails1'
                    }
                  ]
                ]
              }
            },
            {
              width: 200,
              table: {
                widths: [70, 10, 90],
                body: [
                  [
                    { text: 'INVOICE NO', style: 'fontBold' },
                    { text: ':' },
                    { text: invoiceNo }
                  ],
                  [
                    { text: 'DATE', style: 'fontBold' },
                    { text: ':' },
                    { text: invoiceDate }
                  ]
                ]
              },
              layout: {
                defaultBorder: false
              }
            }
          ]
        },
        {
          margin: [0, 15, 0, 15],
          columns: [
            {
              table: {
                widths: [55, 10, '*'],
                body: [
                  [
                    { text: 'Name', style: 'fontBold' },
                    { text: ':' },
                    { text: customer.name }
                  ],
                  [
                    { text: 'Company', style: 'fontBold' },
                    { text: ':' },
                    { text: customer.company }
                  ],
                  [
                    { text: 'Address', style: 'fontBold' },
                    { text: ':' },
                    { text: customer.address }
                  ],
                  [
                    { text: 'Phone', style: 'fontBold' },
                    { text: ':' },
                    { text: customer.phone }
                  ],
                  [
                    { text: 'Email', style: 'fontBold' },
                    { text: ':' },
                    { text: customer.email }
                  ]
                ]
              },
              layout: {
                defaultBorder: false
              }
            }
          ]
        },
        {
          table: {
            widths: ['*', 90, 90, 120],
            body: [
              [
                { text: 'Description', fillColor: '#4472c4', style: 'tableHeader' },
                { text: 'QTY', fillColor: '#4472c4', style: 'tableHeader' },
                { text: 'Unit', fillColor: '#4472c4', style: 'tableHeader' },
                { text: 'Amount', fillColor: '#4472c4', style: 'tableHeader' }
              ],
              [
                { text: 'Charge', fillColor: '#b4c7e7', style: 'tableGroup' },
                { text: ' ', fillColor: '#b4c7e7', style: 'tableGroup' },
                { text: ' ', fillColor: '#b4c7e7', style: 'tableGroup' },
                { text: ' ', fillColor: '#b4c7e7', style: 'tableGroup' }
              ],
              ...chargeRow,
              [
                { text: 'Transfer', fillColor: '#b4c7e7', style: 'tableGroup' },
                { text: ' ', fillColor: '#b4c7e7', style: 'tableGroup' },
                { text: ' ', fillColor: '#b4c7e7', style: 'tableGroup' },
                { text: ' ', fillColor: '#b4c7e7', style: 'tableGroup' }
              ],
              ...transferRow,
              [
                { text: 'Link pay', fillColor: '#b4c7e7', style: 'tableGroup' },
                { text: ' ', fillColor: '#b4c7e7', style: 'tableGroup' },
                { text: ' ', fillColor: '#b4c7e7', style: 'tableGroup' },
                { text: ' ', fillColor: '#b4c7e7', style: 'tableGroup' }
              ],
              ...linkPayRow,
              [
                {
                  text: 'TOTAL SERVICE',
                  fillColor: '#cfd5ea',
                  alignment: 'right',
                  style: 'fontBold'
                },
                { text: ' ', fillColor: '#cfd5ea' },
                { text: ' ', fillColor: '#cfd5ea' },
                {
                  text: totalService,
                  fillColor: '#cfd5ea',
                  alignment: 'right',
                  style: 'fontBold'
                }
              ],
              [
                {
                  text: 'TAX 3%',
                  fillColor: '#cfd5ea',
                  alignment: 'right',
                  style: 'fontBold'
                },
                { text: ' ', fillColor: '#cfd5ea' },
                { text: ' ', fillColor: '#cfd5ea' },
                {
                  text: tax,
                  fillColor: '#cfd5ea',
                  alignment: 'right',
                  style: 'fontBold'
                }
              ],
              [
                {
                  text: 'TOTAL AMOUNT',
                  fillColor: '#cfd5ea',
                  alignment: 'right',
                  style: 'fontBold'
                },
                { text: ' ', fillColor: '#cfd5ea' },
                { text: ' ', fillColor: '#cfd5ea' },
                {
                  text: totalAmount,
                  fillColor: '#cfd5ea',
                  alignment: 'right',
                  style: 'fontBold'
                }
              ]
            ]
          },
          layout: {
            defaultBorder: true,
            hLineColor: function(i, node) {
              return 'white';
            },
            vLineColor: function(i, node) {
              return 'white';
            }
          }
        }
      ],
      styles: {
        fontBold: {
          bold: true
        },
        textCenter: {
          alignment: 'center'
        },
        textRight: {
          alignment: 'right'
        },
        customerDetails1: {
          fontSize: 8
        },
        customerDetails2: {
          fontSize: 20,
          bold: true,
          color: '#FFFFFF'
        },
        tableHeader: {
          fontSize: 13,
          bold: true,
          color: '#FFFFFF'
        },
        tableGroup: {
          bold: true
        }
      },
      defaultStyle: {
        font: 'Roboto'
      }
    };

    const win = window.open('', '_blank');
    pdfMake.createPdf(docDefinition).open({}, win);
    dispatch(BillingPdfNotReady());
  };

  return (
    <div
      {...rest}
      className={clsx(classes.root, className)}
    >
      <Typography
        color="textSecondary"
        gutterBottom
        variant="body2"
      >
        {data.length} Records found. Page {page + 1} of{' '}
        {Math.ceil(data.length / rowsPerPage)}
      </Typography>
      <Card>
        <CardHeader title="Summary" />
        <Divider />
        <CardContent className={classes.content}>
          <PerfectScrollbar>
            <div className={classes.inner}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ textAlign: 'center' }}>
                                            Month/Year
                    </TableCell>
                    <TableCell style={{ textAlign: 'center' }}>
                                            Due Date
                    </TableCell>
                    <TableCell style={{ textAlign: 'center' }}>
                                            Charge
                    </TableCell>
                    <TableCell style={{ textAlign: 'center' }}>
                                            Transfer
                    </TableCell>
                    <TableCell style={{ textAlign: 'center' }}>
                                            Link pay
                    </TableCell>
                    <TableCell style={{ textAlign: 'center' }}>Total</TableCell>
                    <TableCell style={{ textAlign: 'center' }}>
                                            Status
                    </TableCell>
                    <TableCell style={{ textAlign: 'center' }}>
                                            Download
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(record => (
                      <TableRow
                        hover
                        key={record.date}
                      >
                        <TableCell>
                          {moment(record.date).format('MM/YYYY')}
                        </TableCell>
                        <TableCell>
                          {moment(record.date).format('DD/MM/YYYY')}
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                          {`${currency}${record.charge}`}
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                          {`${currency}${record.transfer}`}
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                          {`${currency}${record.linkPay}`}
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                          {`${currency}${record.total}`}
                        </TableCell>
                        <TableCell style={{ textAlign: 'center' }}>
                          {record.status}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            color="primary"
                            disabled={pdfReady}
                            onClick={handleProcessBillingPDF}
                            size="small"
                            variant="outlined"
                          >
                                                        PDF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </PerfectScrollbar>
        </CardContent>
        <CardActions className={classes.actions}>
          <TablePagination
            component="div"
            count={data.length}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </CardActions>
      </Card>
    </div>
  );
};

Results.propTypes = {
  className: PropTypes.string,
  data: PropTypes.array.isRequired
};

Results.defaultProps = {
  data: []
};

export default Results;

const useStyles = makeStyles(theme => ({
  root: {},
  filterButton: {
    marginRight: theme.spacing(2)
  },
  content: {
    padding: 0
  },
  inner: {
    minWidth: 1150
  },
  actions: {
    padding: theme.spacing(0, 1),
    justifyContent: 'flex-end'
  },
  paid: {
    color: colors.green[500]
  },
  reject: {
    color: colors.red[500]
  }
}));
